import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";

import { useArtwork } from "~/api/artwork";
import type {
  ArtistHit,
  DroppedAct,
  OtherDate,
  Performance,
} from "~/api/types";

import { bubbleFor } from "~/components/bubbles";

import s from "./BillSection.module.css";

/**
 * The bill inside the event sheet, ported from the `acts` / `dropped` / add-row block of
 * `_calendar_page.py`'s `openEvent`, and from the `[data-fav]`, `[data-drop]`,
 * `[data-restore]` and `wireAddAct` handlers under it.
 *
 * Three things about this area are decisions rather than markup, and each one is written
 * where it happens below:
 *
 * **A name here goes to the act, not to the gig.** This is the only place on the site where
 * it does. On a card every part — the artwork, the night, the name — goes to `/e/:id`,
 * because linking a bill of four offered four destinations and none of them was the gig the
 * reader was looking at. Inside the sheet you are already *on* the gig, so the only thing a
 * name can usefully answer is "who are they, and when else are they playing".
 *
 * **The star is the control that actually removes one of these gigs from the list.** A gig
 * is on Curated for two reasons that look identical on a card: the editor added it, or a
 * followed act headlines it. The heart un-adds the first and cannot touch the second — the
 * next ranking puts it straight back, because the reason it is there is the following. So
 * the heart on a starred-headliner row is a button that appears to do nothing, and this is
 * where the fact behind it is changed.
 *
 * **Every control on this bill is the editor's.** `POST /bill` and `POST /favourite` are
 * both 403 for a signed-in non-editor and 401 for a stranger — favouriting moves
 * `curation_status` across every gig an act headlines, which is the published calendar. A
 * reader sees the bill, the roles and the way through to each act, and no buttons.
 */

/** The signed-in viewer. Local by request; only `isEditor` is read. */
export interface Me {
  id: number;
  name: string;
  username: string;
  isEditor: boolean;
}

/** `wireAddAct`'s 180ms. Exported so a test can wait exactly one debounce. */
export const FIND_DELAY_MS = 180;

export interface BillSectionProps {
  /**
   * `GET /event/:id` → `acts`. Placeholders included; they render inert.
   *
   * In the order to draw them: headliner first, then most-listened. Sorted by the server
   * because that is where the listener counts are — sorting here would mean shipping a
   * number to the browser so the browser could put the list back in the order the query
   * could have produced.
   */
  acts: Performance[];
  /** Acts taken off this bill by hand. The server sends `[]` to anyone but the editor. */
  dropped: DroppedAct[];
  me: Me | null;

  /**
   * Artist ids of every followed act — the payload's `favourites`, which decides whether
   * each star is lit.
   *
   * NOT declared as a prop for decoration: without it every star renders unpressed, which
   * is the one lie this control must not tell, and nothing in `Performance` carries the
   * fact (`is_highlight` is the bill's own emphasis, not your following).
   *
   * **It is a list of ids, despite `EventDetailPublic["favourites"]` typing it as
   * `Record<string, string>`.** The server sends `list(calendar.favourite_artists(conn))`,
   * and `list()` of a dict is its keys — so the wire carries `[441, 903]`, not an object.
   * `Object.keys(detail.favourites)` on the real payload returns array *indices*: pass
   * `detail.favourites as unknown as number[]` until types.ts is corrected.
   */
  favourites: readonly number[];

  /**
   * `GET /event/:id` → `other_dates`. Only the per-act count is used, for the `· +2` that
   * says this act plays elsewhere in the window; the section that lists them is not this
   * one's. Optional because its absence costs a hint and nothing else.
   */
  otherDates?: readonly OtherDate[];

  /** Open this act's page. A callback, not a link: the artist opens inside the sheet. */
  onArtist(artistId: number): void;

  /**
   * Follow this act, or stop. `on` is the state being ASKED FOR — it goes straight into
   * `POST /favourite {artist, favourite: on}`, like `api.save(id, saved)` beside it. The
   * Python read the current state off `aria-pressed` at the call site and posted its
   * negation; that inversion lives here now, once, rather than in the caller.
   */
  onStar(artistId: number, on: boolean): void;

  /**
   * A name nobody in the database has: `POST /bill {name}`, which resolves the name and
   * creates an act if it has to.
   *
   * Separate from `onPick` on purpose, because `calendar.bill` is not symmetric: it starts
   * with `if name is not None: artist_id = artists.resolve(conn, name).artist_id`, so a
   * request carrying BOTH keys throws the id away and re-resolves the text. Sending
   * `{artist, name}` together would quietly recreate the duplicate-artist problem that
   * suggesting-as-you-type exists to prevent.
   */
  onAdd(name: string): void;

  /**
   * An act the database already has: `POST /bill {artist, on: true}`. Both the suggestion
   * list and the undo arrow on a dropped act come through here — putting one back is the
   * same request as adding one that was never listed.
   */
  onPick(artistId: number): void;

  /** Take a name off this bill: `POST /bill {artist, on: false}`. A flag, not a delete. */
  onDrop(artistId: number): void;

  /**
   * `GET /find?kind=artists&q=` for the picker. A prop rather than a call, because this
   * component makes no requests: every write above is the sheet's to send, and `api` in
   * src/api/client.ts is what sends them, with the credentials and the 401 that the old
   * page re-implemented per call site.
   */
  onFind(query: string): Promise<ArtistHit[]>;
}

/**
 * An act's face on the bill: their photograph, then a poster, then a bubble.
 *
 * Three steps, because two of them were missing. Only about one act in six has a portrait,
 * and the bill went straight from that to a bubble — while the artist page for the very
 * same act fell back to the poster of a night they headline. So an act had a picture on one
 * page and a bubble on the other, which was reported for Walt Disco, closed as "they have
 * no portrait anywhere", and reported again for ADÉLA. Both surfaces answer the same way
 * now, and `poster` carries the artist page's own rule so they cannot drift.
 *
 * A component rather than an expression because `useArtwork` is a hook and this is drawn
 * inside a map. It earns its place: a poster is an event image, and Ticketmaster has no
 * generated variant for about a tenth of its assets — so the sized URL 403s and the step
 * down to the original is what stops a face from being a broken image.
 */
function Face({ act }: { act: Performance }) {
  const art = useArtwork(act.portrait ?? act.poster ?? null);
  return (
    <span className={s.shot}>
      <img
        className={s.face}
        src={art.src && !art.broken ? art.src : bubbleFor(`a-${act.artist_id}`)}
        alt=""
        loading="lazy"
        decoding="async"
        onError={art.onError}
      />
    </span>
  );
}

export function BillSection({
  acts,
  dropped,
  me,
  favourites,
  otherDates = [],
  onArtist,
  onStar,
  onAdd,
  onPick,
  onDrop,
  onFind,
}: BillSectionProps) {
  const editor = me?.isEditor === true;
  const followed = new Set(favourites);

  /** How many other nights this act plays inside the window. `countFor` in the Python. */
  const countFor = (artistId: number) =>
    otherDates.filter((d) => d.artist_id === artistId).length;

  return (
    <div className={s.sec}>
      <h3>Line-up</h3>

      <div className={s.bill}>
        {acts.length === 0 ? (
          <div className={s.spin}>No line-up listed.</div>
        ) : (
          acts.map((a) =>
            a.is_placeholder ? (
              // A slot the venue announced without naming. There is no artist to open and
              // nothing to follow, so the row is text.
              <div
                key={`${a.artist_id}-${a.role}`}
                className={[s.act, s.ph, a.role === "headliner" ? s.lead : ""]
                  .filter(Boolean)
                  .join(" ")}
              >
                <span>{a.name}</span>
                {/* A placeholder is the listing declining to name somebody, so "not
                    announced" is the whole of what is known — and the role that used to sit
                    in front of it was the same unbacked claim as on a named act. */}
                <span className={s.r}>not announced</span>
              </div>
            ) : (
              <div
                key={`${a.artist_id}-${a.role}`}
                className={[
                  s.act,
                  editor ? s.named : s.reading,
                  // The top of the bill keeps the full width, so a grid still reads as a
                  // line-up rather than as an alphabet of equals.
                  a.role === "headliner" ? s.lead : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {/*
                 * Anybody signed in. Following an act is a thing a reader does to their own
                 * calendar — it ranks their view and nobody else's — and the star was the
                 * editor's alone only because there was one boolean per act for the whole
                 * site. There is a row per person now.
                 */}
                {me ? (
                  <FollowStar
                    artistId={a.artist_id}
                    on={followed.has(a.artist_id)}
                    adopts={editor}
                    onStar={onStar}
                  />
                ) : null}

                {/*
                 * The one name on the site that leaves the gig it is printed on. A button
                 * rather than an <a>, and so not an `AppLink`: `openArtist` replaces the
                 * sheet's contents in place and offers a way back to this event, which an
                 * address the router owns would not.
                 */}
                {/*
                 * The one name on the site that leaves the gig it is printed on. A button
                 * rather than an <a>, and so not an `AppLink`: `openArtist` replaces the
                 * sheet's contents in place and offers a way back to this event, which an
                 * address the router owns would not.
                 *
                 * Shaped like a card on the front page, because that is what it is: a
                 * picture, then a name, then a line of small type. It was a row with a 20px
                 * thumbnail beside the name, which is a favicon rather than a photograph —
                 * and a line-up is the one place on a gig's page where the acts are the
                 * subject rather than the label.
                 */}
                <button
                  type="button"
                  className={s.artistlink}
                  onClick={() => onArtist(a.artist_id)}
                >
                  <Face act={a} />
                  <span className={s.who}>{a.name}</span>
                  {/*
                   * What kind of set it is, where somebody has said. It used to be part of
                   * the name — `Hannah Diamond [LIVE]` was a different act from `Hannah
                   * Diamond [DJ SET]`, with its own page and half her dates — and folding
                   * those two together is only right if this survives the fold.
                   */}
                  {a.qualifier ? (
                    <span className={s.kind}>{a.qualifier}</span>
                  ) : null}
                </button>

                <span className={`${s.r} ${s.dates}`}>
                  {/*
                   * No role here at all, on purpose.
                   *
                   * This printed `headliner` and `support` unconditionally, which was
                   * reported from the far end: a listing billing the only band on it as
                   * support. `Performance.role_stated` now says whether anybody actually
                   * stated a role — a source has to have said so, and the bill has to have
                   * somebody to lead and more than one name to be led — and on this surface
                   * the answer is not used, because the owner's call was to take the words
                   * off the gig's page rather than show them for the 2.3% that qualify.
                   *
                   * The reasoning holds without the flag: a bill is a list in billing order
                   * and the order is right there to read, so the words restate the layout
                   * and, on the 97.7%, restate it wrongly. An act's own page is the surface
                   * that still says it — there the role describes one booking among many
                   * rather than labelling a list the reader can already see — and it is
                   * gated on `role_stated` there.
                   *
                   * To bring it back: `{a.role_stated ? a.role : ''}`, here.
                   */}
                  {/*
                   * What else this act is playing, said rather than abbreviated.
                   *
                   * It read `+2`, which is a number with no noun: it could as easily have
                   * been a price, a count of tickets, or the two acts a `+` in a title
                   * usually joins. This line has room for the words and nothing else to say.
                   *
                   * The digest's `★` used to sit here and does not any more. `is_highlight`
                   * marks which act a summary email names — editorial bookkeeping, not a
                   * fact about the bill — and it was drawn as a filled star directly under
                   * the hollow one that follows the act, so the row appeared to offer the
                   * same control twice in two states. The digest and the curate page still
                   * read the column; a reader has no use for it.
                   */}
                  {countFor(a.artist_id)
                    ? `${countFor(a.artist_id)} more ${
                        countFor(a.artist_id) === 1 ? "date" : "dates"
                      } scheduled`
                    : ""}
                  {editor ? (
                    <>
                      {" "}
                      <button
                        type="button"
                        className={s.drop}
                        aria-label="Not on this line-up"
                        title="Not on this line-up"
                        onClick={() => onDrop(a.artist_id)}
                      >
                        ×
                      </button>
                    </>
                  ) : null}
                </span>
              </div>
            ),
          )
        )}
      </div>

      {/*
       * What a person has taken off this bill, struck through, each with the way back.
       * `GET /event/:id` already empties `dropped` for anyone who is not the editor; the
       * gate is stated here as well because this block is nothing but a control, and a
       * server that stopped emptying it would otherwise hand every reader a button that
       * 403s.
       */}
      {editor && dropped.length > 0 ? (
        <div className={s.dropped}>
          {dropped.map((d) => (
            <span key={`${d.artist_id}-${d.role}`} className={s.gone}>
              {d.name}{" "}
              <button
                type="button"
                className={s.drop}
                aria-label="Put them back on this line-up"
                title="Put them back on this line-up"
                onClick={() => onPick(d.artist_id)}
              >
                ↩
              </button>
            </span>
          ))}
        </div>
      ) : null}

      {editor ? <AddAct onAdd={onAdd} onPick={onPick} onFind={onFind} /> : null}
    </div>
  );
}

/**
 * One act's follow star.
 *
 * Its own component so the negation happens in exactly one place. The Python asked the DOM
 * — `b.getAttribute('aria-pressed') === 'true'` — which is the same value read back out of
 * the attribute it had just written, and is why the inversion was easy to get backwards.
 */
function FollowStar({
  artistId,
  on,
  adopts,
  onStar,
}: {
  artistId: number;
  on: boolean;
  /**
   * Whether this press also publishes.
   *
   * The same gesture does two different things depending on whose calendar it is, and the
   * label has to say which — the editor's follow adopts every gig the act headlines onto the
   * calendar everybody reads, and a reader's ranks their own view and reaches nobody. A
   * label that promised adoption to a reader would be describing a consequence that is not
   * going to happen.
   */
  adopts: boolean;
  onStar: (artistId: number, on: boolean) => void;
}) {
  const label = adopts
    ? on
      ? "Everything by them is added automatically"
      : "Add everything by them, now and in future"
    : on
      ? "Following them. Stop"
      : "Follow them, so their dates rank higher for you";
  return (
    <button
      type="button"
      className={s.fav}
      aria-pressed={on}
      // The Python emitted `title` alone, and a title is a description rather than a name:
      // the accessible name of this button was the glyph, so it announced as "star button".
      // The label is added, in the title's own words, because a control that matters this
      // much cannot be reachable only by readers who can see which of ★ and ☆ is on screen.
      aria-label={label}
      title={label}
      onClick={() => onStar(artistId, !on)}
    >
      {on ? "★" : "☆"}
    </button>
  );
}

/**
 * Type a name, see who the database already has, click one.
 *
 * Suggesting is not a convenience here. `artists.resolve` creates a row for any name it
 * cannot match exactly, so a typed `Bloc Pary` becomes a second Bloc Party rather than an
 * error and nothing downstream ever notices. Picking a suggestion sends an id, which cannot
 * invent anybody. Typing still works — an act genuinely not in the database has to get in
 * somehow — it is just now the second-easiest thing to do rather than the only one.
 */
function AddAct({
  onAdd,
  onPick,
  onFind,
}: {
  onAdd: (name: string) => void;
  onPick: (artistId: number) => void;
  onFind: (query: string) => Promise<ArtistHit[]>;
}) {
  const [query, setQuery] = useState("");
  /** `null` is the list hidden; `[]` is the list open saying nobody has that name. */
  const [hits, setHits] = useState<ArtistHit[] | null>(null);

  // Held in a ref so that a parent re-rendering with a fresh arrow does not restart the
  // debounce on a search the reader has already finished typing.
  const find = useRef(onFind);
  useEffect(() => {
    find.current = onFind;
  });

  useEffect(() => {
    const asked = query.trim();
    if (!asked) {
      setHits(null);
      return;
    }
    let live = true;
    const timer = setTimeout(() => {
      find
        .current(asked)
        // The answer belongs to the query that asked for it, or the list shows the previous
        // word's acts under this one's text. `live` is React's spelling of the Python's
        // re-read of the input before painting.
        .then((found) => {
          if (live) setHits(found);
        })
        // A failed search closes the list rather than claiming nobody has that name. It
        // cannot be a 401: nothing here renders for anyone who is not the editor.
        .catch(() => {
          if (live) setHits(null);
        });
    }, FIND_DELAY_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [query]);

  /** The Add button and a bare Enter. A blank box is not a submission. */
  const go = () => {
    if (query.trim()) onAdd(query.trim());
  };

  const take = (artistId: number) => {
    setHits(null);
    onPick(artistId);
  };

  function keyDown(ev: KeyboardEvent<HTMLInputElement>) {
    if (ev.key === "Escape") {
      // Not stopped, as in the Python: the sheet around this is a <dialog>, and Escape
      // closing it is the behaviour a dialog owes its reader. Escape here shuts the
      // suggestion list on the way past.
      setHits(null);
      return;
    }
    if (ev.key !== "Enter") return;
    // Enter takes the top suggestion when there is one, because that is what the list is
    // for; a name nobody has is still one Add away.
    const first = hits?.[0];
    if (first) take(first.id);
    else go();
  }

  return (
    <div className={`${s.editrow} ${s.addrow}`}>
      <input
        className={s.addAct}
        value={query}
        placeholder="Add an act to this line-up"
        autoComplete="off"
        onChange={(ev) => setQuery(ev.target.value)}
        onKeyDown={keyDown}
      />
      <button type="button" className={s.bact} onClick={go}>
        Add
      </button>
      <div className={s.addActList} hidden={hits === null}>
        {hits !== null && hits.length === 0 ? (
          <div className={s.none}>
            Nobody by that name yet — Add makes them.
          </div>
        ) : null}
        {(hits ?? []).map((a) => (
          <button key={a.id} type="button" onClick={() => take(a.id)}>
            <span>
              {a.is_favourite ? "★ " : ""}
              {a.name}
            </span>
            <span className={s.c}>
              {a.appearances} {a.appearances === 1 ? "date" : "dates"}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
